import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { format } from 'date-fns'
import {
  Grid,
  Typography,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  PageContainer,
  StyledCard,
  SectionHeader,
  PrimaryButton,
} from '../components/ui/StyledComponents'
import EditGuardianForm from '../components/admin/EditGuardianForm'
import ArchiveDialog from '../components/admin/ArchiveDialog'
import SnackbarMessage from '../components/SnackbarMessage'
import { useAuth } from '../context/AuthContext'
import supabase from '../supabaseClient'

function ParentDetailPage() {
  const { parentId } = useParams()
  const [parent, setParent] = useState(null)
  const [children, setChildren] = useState([])
  const [loading, setLoading] = useState(true)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  })
  const { userRole, user } = useAuth()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isAdmin = userRole === 'admin' || userRole === 'super_admin'

  // Fetch parent demographic info
  useEffect(() => {
    const fetchParentInfo = async () => {
      setLoading(true)
      const { data, error } = await supabase
        .from('guardians')
        .select('*')
        .eq('id', parentId)
        .single()

      if (error) {
        console.error('Error fetching parent info:', error)
      } else {
        setParent(data)
      }
      setLoading(false)
    }

    fetchParentInfo()
  }, [parentId])

  // Fetch children associated with this parent through student_guardians
  useEffect(() => {
    const fetchChildren = async () => {
      if (!parent) return

      const { data, error } = await supabase
        .from('student_guardians')
        .select(`
          id,
          student_id,
          relationship_type,
          is_primary,
          students (*)
        `)
        .eq('guardian_id', parentId)
        .order('is_primary', { ascending: false })

      if (error) {
        console.error('Error fetching children:', error)
      } else {
        // Transform the data to match the expected format
        const transformedData = data.map(item => ({
          ...item.students,
          relationship_type: item.relationship_type,
          is_primary: item.is_primary,
          student_guardian_id: item.id
        }))
        setChildren(transformedData)
      }
    }

    if (parent) {
      fetchChildren()
    }
  }, [parent, parentId])

  // Convert relationship from parent's perspective to child's perspective
  const getChildRelationship = (relationshipType, childGender) => {
    const relationship = relationshipType?.toLowerCase() || ''
    const gender = childGender?.toLowerCase() || ''
    
    if (relationship === 'father') {
      return gender === 'male' ? 'Son' : 'Daughter'
    } else if (relationship === 'mother') {
      return gender === 'male' ? 'Son' : 'Daughter'
    } else {
      // For 'Other' or unknown relationship types, return as-is or 'Child'
      return relationshipType ? relationshipType.charAt(0).toUpperCase() + relationshipType.slice(1) : 'Child'
    }
  }

  const handleEditSuccess = () => {
    // Refresh parent data
    const fetchParentInfo = async () => {
      const { data, error } = await supabase
        .from('guardians')
        .select('*')
        .eq('id', parentId)
        .single()

      if (error) {
        console.error('Error fetching parent info:', error)
      } else {
        setParent(data)
      }
    }

    fetchParentInfo()
  }

  const handleToggleActive = () => {
    if (!parent?.id) return

    // If currently active and trying to inactivate, show archive dialog
    if (parent.is_active !== false) {
      setArchiveDialogOpen(true)
    } else {
      // If reactivating, proceed directly (no dialog needed)
      handleReactivate()
    }
  }

  const handleArchiveConfirm = async (reason) => {
    if (!parent?.id) return

    setArchiveDialogOpen(false)
    setActionLoading(true)

    try {
      const { error: updateError } = await supabase
        .from('guardians')
        .update({
          is_active: false,
          archive_reason: reason,
          archived_by: user?.id || null,
          archived_at: new Date().toISOString(),
        })
        .eq('id', parent.id)

      if (updateError) throw updateError

      // Refresh parent data
      const { data, error: fetchError } = await supabase
        .from('guardians')
        .select('*')
        .eq('id', parent.id)
        .single()

      if (fetchError) throw fetchError
      setParent(data)

      setSnackbar({
        open: true,
        message: 'Guardian inactivated successfully',
        severity: 'success',
      })
    } catch (error) {
      console.error('Error inactivating guardian:', error)
      setSnackbar({
        open: true,
        message: 'Failed to inactivate guardian',
        severity: 'error',
      })
    } finally {
      setActionLoading(false)
    }
  }

  const handleReactivate = async () => {
    if (!parent?.id) return

    setActionLoading(true)

    try {
      const { error: updateError } = await supabase
        .from('guardians')
        .update({
          is_active: true,
          archive_reason: null,
          archived_by: null,
          archived_at: null,
        })
        .eq('id', parent.id)

      if (updateError) throw updateError

      // Refresh parent data
      const { data, error: fetchError } = await supabase
        .from('guardians')
        .select('*')
        .eq('id', parent.id)
        .single()

      if (fetchError) throw fetchError
      setParent(data)

      setSnackbar({
        open: true,
        message: 'Guardian reactivated successfully',
        severity: 'success',
      })
    } catch (error) {
      console.error('Error reactivating guardian:', error)
      setSnackbar({
        open: true,
        message: 'Failed to reactivate guardian',
        severity: 'error',
      })
    } finally {
      setActionLoading(false)
    }
  }

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  return (
    <PageContainer>
      {loading ? (
        <StyledCard sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
          <Typography 
            variant="body1"
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            Loading parent details...
          </Typography>
        </StyledCard>
      ) : parent ? (
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {/* Parent Demographic Information */}
          <Grid item xs={12} sm={4}>
            <StyledCard sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
              <SectionHeader sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }, mb: { xs: 1.5, sm: 2 } }}>
                Parent/Guardian Info
              </SectionHeader>
              <Box sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-start' }, mb: { xs: 1.5, sm: 2 } }}>
                <Avatar
                  alt={`${parent.first_name} ${parent.middle_name ? `${parent.middle_name} ` : ''}${parent.last_name}`}
                  src={parent.photo_url}
                  sx={{ 
                    width: { xs: 100, sm: 110, md: 120 }, 
                    height: { xs: 100, sm: 110, md: 120 },
                  }}
                />
              </Box>
              <Typography 
                variant="h6"
                sx={{ 
                  fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
                  textAlign: { xs: 'center', sm: 'left' },
                }}
              >
                {parent.first_name} {parent.middle_name ? `${parent.middle_name} ` : ''}{parent.last_name}
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mt: 1,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  textAlign: { xs: 'center', sm: 'left' },
                }}
              >
                <strong>Email:</strong> {parent.email || 'Not provided'}
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mt: 1,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  textAlign: { xs: 'center', sm: 'left' },
                }}
              >
                <strong>Phone:</strong> {parent.phone || 'Not provided'}
              </Typography>
              {parent.gender && (
                <Typography 
                  variant="body1" 
                  sx={{ 
                    mt: 1,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    textAlign: { xs: 'center', sm: 'left' },
                  }}
                >
                  <strong>Gender:</strong> {parent.gender}
                </Typography>
              )}
              {parent.date_of_birth && (
                <Typography 
                  variant="body1" 
                  sx={{ 
                    mt: 1,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    textAlign: { xs: 'center', sm: 'left' },
                  }}
                >
                  <strong>Date of Birth:</strong>{' '}
                  {format(new Date(parent.date_of_birth), 'MM/dd/yyyy')}
                </Typography>
              )}
              {parent.created_at && (
                <Typography 
                  variant="body1" 
                  sx={{ 
                    mt: 1,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    textAlign: { xs: 'center', sm: 'left' },
                  }}
                >
                  <strong>Registered:</strong>{' '}
                  {format(new Date(parent.created_at), 'MM/dd/yyyy')}
                </Typography>
              )}
              {parent.address && (
                <Typography 
                  variant="body1" 
                  sx={{ 
                    mt: 1,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    textAlign: { xs: 'center', sm: 'left' },
                  }}
                >
                  <strong>Address:</strong> {parent.address}
                </Typography>
              )}
              {isAdmin && (
                <>
                  <PrimaryButton
                    variant="contained"
                    onClick={() => setIsEditDialogOpen(true)}
                    fullWidth
                    sx={{ 
                      mt: 2,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      padding: { xs: '10px 16px', sm: '12px 24px' },
                    }}
                  >
                    Edit Guardian Info
                  </PrimaryButton>
                  <Button
                    variant={parent.is_active !== false ? 'outlined' : 'contained'}
                    color={parent.is_active !== false ? 'error' : 'success'}
                    onClick={handleToggleActive}
                    disabled={actionLoading}
                    fullWidth
                    sx={{ 
                      mt: 1,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      padding: { xs: '10px 16px', sm: '12px 24px' },
                    }}
                  >
                    {actionLoading ? (
                      <CircularProgress size={isMobile ? 20 : 24} />
                    ) : parent.is_active !== false ? (
                      'Inactivate Guardian'
                    ) : (
                      'Reactivate Guardian'
                    )}
                  </Button>
                </>
              )}
            </StyledCard>
          </Grid>

          {/* Children List */}
          <Grid item xs={12} sm={8}>
            <StyledCard sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
              <SectionHeader sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }, mb: { xs: 1.5, sm: 2 } }}>
                Children
              </SectionHeader>
              <TableContainer
                sx={{
                  overflowX: { xs: 'auto', sm: 'visible' },
                  '&::-webkit-scrollbar': {
                    height: { xs: '6px', sm: '0px' },
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '3px',
                  },
                }}
              >
                <Table sx={{ minWidth: { xs: 700, sm: 'auto' } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, whiteSpace: 'nowrap' }}>
                        Name
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' } }}>
                        Relationship
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' } }}>
                        Gender
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' } }}>
                        Date of Birth
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, whiteSpace: 'nowrap' }}>
                        Status
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, whiteSpace: 'nowrap' }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {children.length > 0 ? (
                      children.map((child) => (
                        <TableRow 
                          key={child.id}
                        >
                          <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            <Box>
                              <Typography variant="body2" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                {child.first_name} {child.last_name}
                              </Typography>
                              {isMobile && (
                                <>
                                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem' }}>
                                    Relationship: {getChildRelationship(child.relationship_type, child.gender)}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem' }}>
                                    Gender: {child.gender}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem' }}>
                                    DOB: {child.date_of_birth
                                      ? format(new Date(child.date_of_birth), 'MM/dd/yyyy')
                                      : 'Not provided'}
                                  </Typography>
                                </>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, display: { xs: 'none', md: 'table-cell' } }}>
                            {getChildRelationship(child.relationship_type, child.gender)}
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, display: { xs: 'none', lg: 'table-cell' } }}>
                            {child.gender}
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, display: { xs: 'none', lg: 'table-cell' } }}>
                            {child.date_of_birth
                              ? format(new Date(child.date_of_birth), 'MM/dd/yyyy')
                              : 'Not provided'}
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            <Chip
                              label={child.is_active !== false ? 'Active' : 'Inactive'}
                              color={child.is_active !== false ? 'success' : 'default'}
                              size="small"
                              sx={{
                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                height: { xs: 20, sm: 24 },
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            <PrimaryButton
                              variant="contained"
                              component={Link}
                              to={`/student_details/${child.id}`}
                              fullWidth={isMobile}
                              sx={{
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                padding: { xs: '8px 12px', sm: '10px 16px' },
                                minWidth: { xs: 'auto', sm: '150px' },
                              }}
                            >
                              {isMobile ? 'View' : 'View Student Details'}
                            </PrimaryButton>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell 
                          colSpan={6} 
                          align="center"
                          sx={{ 
                            py: { xs: 3, sm: 4 },
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                          }}
                        >
                          No children associated with this parent
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </StyledCard>
          </Grid>

          {/* Additional Information Section */}
          <Grid item xs={12}>
            <StyledCard sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
              <SectionHeader sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }, mb: { xs: 1.5, sm: 2 } }}>
                Additional Information
              </SectionHeader>
              <Typography 
                variant="body1"
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                {parent.notes || 'No additional information available.'}
              </Typography>
            </StyledCard>
          </Grid>
        </Grid>
      ) : (
        <StyledCard sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
          <Typography 
            variant="body1"
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            Parent not found
          </Typography>
        </StyledCard>
      )}

      {/* Edit Dialog */}
      <EditGuardianForm
        guardian={parent}
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        onSuccess={handleEditSuccess}
      />

      {/* Archive Dialog */}
      <ArchiveDialog
        open={archiveDialogOpen}
        onClose={() => setArchiveDialogOpen(false)}
        onConfirm={handleArchiveConfirm}
        entityName="Guardian"
      />

      {/* Snackbar */}
      <SnackbarMessage
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
      />
    </PageContainer>
  )
}

export default ParentDetailPage 